import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const EMAIL_THREAD_PARAM = 'emailThread';
const EMAIL_THREAD_HISTORY_KEY = 'emailThreadEntry';

export interface OpenEmailThread {
  /** Drops the thread param on the current history entry. A hash deep link uses this so Back stays put. */
  clearEmailThread: () => void;
  closeEmailThread: () => void;
  /** Activity item id of the email thread open in the activity panel. */
  openEmailItemId: UUID | null;
  openEmailThread: (activityItemId: UUID) => void;
}

/**
 * The email thread open in the activity panel, kept in the URL so a reload lands on the same
 * view. Opening pushes a history entry. Closing pops that entry, so the browser Back button
 * does not land on a second copy of the list. A thread opened by loading the URL has no pushed
 * entry, so closing it replaces the current one.
 */
export function useOpenEmailThread(): OpenEmailThread {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openEmailItemId = searchParams.get(EMAIL_THREAD_PARAM) || null;

  const openEmailThread = useCallback(
    (activityItemId: UUID) =>
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          next.set(EMAIL_THREAD_PARAM, activityItemId);
          return next;
        },
        { state: withEmailThreadHistory(location.state) }
      ),
    [location.state, setSearchParams]
  );

  const clearEmailThread = useCallback(
    () =>
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          next.delete(EMAIL_THREAD_PARAM);
          return next;
        },
        { replace: true, state: withoutEmailThreadHistory(location.state) }
      ),
    [location.state, setSearchParams]
  );

  const closeEmailThread = useCallback(() => {
    if (getIsEmailThreadHistoryEntry(location.state)) {
      navigate(-1);
      return;
    }
    clearEmailThread();
  }, [clearEmailThread, location.state, navigate]);

  return { clearEmailThread, closeEmailThread, openEmailItemId, openEmailThread };
}

function getIsEmailThreadHistoryEntry(state: unknown): boolean {
  const entry = Object.entries(getHistoryState(state)).find(
    ([key]) => key === EMAIL_THREAD_HISTORY_KEY
  );
  return entry?.[1] === true;
}

function withEmailThreadHistory(state: unknown): Record<string, unknown> {
  return { ...getHistoryState(state), [EMAIL_THREAD_HISTORY_KEY]: true };
}

function withoutEmailThreadHistory(state: unknown): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(getHistoryState(state)).filter(([key]) => key !== EMAIL_THREAD_HISTORY_KEY)
  );
}

function getHistoryState(state: unknown): Record<string, unknown> {
  if (!state || typeof state !== 'object') return {};
  return Object.fromEntries(Object.entries(state));
}
